import React, { useEffect, useRef } from 'react';
import { ParticleEffectType } from '../types';

interface ParticleEffectProps {
  type: ParticleEffectType;
  intensity: number; // 0-100
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  rotation: number;
  rotationSpeed: number;
}

const ParticleEffect: React.FC<ParticleEffectProps> = ({ type, intensity }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    if (type === 'none') return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Calculate particle count based on intensity
    const particleCount = Math.floor((intensity / 100) * 150) + 20; // 20-170 particles

    // Initialize particles
    particlesRef.current = Array.from({ length: particleCount }, () => createParticle(canvas.width, canvas.height, type));

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particlesRef.current.forEach((particle) => {
        updateParticle(particle, canvas.width, canvas.height, type);
        drawParticle(ctx, particle, type);
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [type, intensity]);

  if (type === 'none') return null;

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none"
      style={{ zIndex: -1 }}
    />
  );
};

// Create a particle based on effect type
const createParticle = (width: number, height: number, type: ParticleEffectType): Particle => {
  switch (type) {
    case 'snow':
      return {
        x: Math.random() * width,
        y: Math.random() * height - height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: Math.random() * 1 + 0.5,
        size: Math.random() * 3 + 1,
        opacity: Math.random() * 0.6 + 0.2,
        rotation: 0,
        rotationSpeed: 0,
      };

    case 'stars':
      return {
        x: Math.random() * width,
        y: Math.random() * height,
        vx: 0,
        vy: 0,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.8 + 0.2,
        rotation: 0,
        rotationSpeed: 0,
      };

    case 'fireflies':
      return {
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size: Math.random() * 3 + 2,
        opacity: Math.random() * 0.8 + 0.2,
        rotation: 0,
        rotationSpeed: 0,
      };

    case 'sakura':
      return {
        x: Math.random() * width,
        y: Math.random() * height - height,
        vx: (Math.random() - 0.5) * 1,
        vy: Math.random() * 0.5 + 0.3,
        size: Math.random() * 8 + 4,
        opacity: Math.random() * 0.7 + 0.3,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 2,
      };

    case 'embers':
      return {
        x: Math.random() * width,
        y: height + Math.random() * 100,
        vx: (Math.random() - 0.5) * 0.5,
        vy: -(Math.random() * 1 + 0.5),
        size: Math.random() * 3 + 1,
        opacity: Math.random() * 0.8 + 0.2,
        rotation: 0,
        rotationSpeed: 0,
      };

    default:
      return {
        x: Math.random() * width,
        y: Math.random() * height,
        vx: 0,
        vy: 0,
        size: 2,
        opacity: 0.5,
        rotation: 0,
        rotationSpeed: 0,
      };
  }
};

// Update particle position
const updateParticle = (particle: Particle, width: number, height: number, type: ParticleEffectType) => {
  particle.x += particle.vx;
  particle.y += particle.vy;
  particle.rotation += particle.rotationSpeed;

  switch (type) {
    case 'snow':
      // Wrap around
      if (particle.y > height) {
        particle.y = -10;
        particle.x = Math.random() * width;
      }
      if (particle.x > width) particle.x = 0;
      if (particle.x < 0) particle.x = width;
      break;

    case 'stars':
      // Twinkling effect
      particle.opacity = 0.2 + Math.abs(Math.sin(Date.now() * 0.001 + particle.x)) * 0.8;
      break;

    case 'fireflies':
      // Random movement with occasional direction changes
      if (Math.random() < 0.02) {
        particle.vx = (Math.random() - 0.5) * 0.3;
        particle.vy = (Math.random() - 0.5) * 0.3;
      }
      // Pulsing glow
      particle.opacity = 0.3 + Math.abs(Math.sin(Date.now() * 0.003 + particle.x)) * 0.7;
      // Wrap around
      if (particle.x > width) particle.x = 0;
      if (particle.x < 0) particle.x = width;
      if (particle.y > height) particle.y = 0;
      if (particle.y < 0) particle.y = height;
      break;

    case 'sakura':
      // Swaying motion
      particle.vx = Math.sin(Date.now() * 0.001 + particle.y) * 0.5;
      // Wrap around
      if (particle.y > height) {
        particle.y = -10;
        particle.x = Math.random() * width;
      }
      if (particle.x > width) particle.x = 0;
      if (particle.x < 0) particle.x = width;
      break;

    case 'embers':
      // Upward with occasional flicker
      if (Math.random() < 0.05) {
        particle.vx = (Math.random() - 0.5) * 0.5;
      }
      // Fade out as they rise
      particle.opacity = Math.max(0, particle.opacity - 0.002);
      // Reset if off screen or faded
      if (particle.y < -10 || particle.opacity <= 0) {
        particle.y = height + Math.random() * 100;
        particle.x = Math.random() * width;
        particle.opacity = Math.random() * 0.8 + 0.2;
      }
      break;
  }
};

// Draw particle
const drawParticle = (ctx: CanvasRenderingContext2D, particle: Particle, type: ParticleEffectType) => {
  ctx.save();
  ctx.globalAlpha = particle.opacity;
  ctx.translate(particle.x, particle.y);
  ctx.rotate((particle.rotation * Math.PI) / 180);

  switch (type) {
    case 'snow':
      // Simple white circle
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(0, 0, particle.size, 0, Math.PI * 2);
      ctx.fill();
      break;

    case 'stars':
      // Small star shape
      ctx.fillStyle = '#ffffff';
      ctx.shadowBlur = 4;
      ctx.shadowColor = '#ffffff';
      ctx.beginPath();
      ctx.arc(0, 0, particle.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      break;

    case 'fireflies':
      // Glowing circle
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, particle.size * 2);
      gradient.addColorStop(0, 'rgba(255, 220, 100, 0.8)');
      gradient.addColorStop(0.5, 'rgba(255, 200, 50, 0.4)');
      gradient.addColorStop(1, 'rgba(255, 180, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(0, 0, particle.size * 2, 0, Math.PI * 2);
      ctx.fill();
      break;

    case 'sakura':
      // Petal shape
      ctx.fillStyle = '#ffb7c5';
      ctx.beginPath();
      // Simple 5-petal flower
      for (let i = 0; i < 5; i++) {
        const angle = (i * Math.PI * 2) / 5;
        const x = Math.cos(angle) * particle.size;
        const y = Math.sin(angle) * particle.size;
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.closePath();
      ctx.fill();
      break;

    case 'embers':
      // Glowing ember
      const emberGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, particle.size * 2);
      emberGradient.addColorStop(0, 'rgba(255, 150, 50, 1)');
      emberGradient.addColorStop(0.5, 'rgba(255, 100, 30, 0.6)');
      emberGradient.addColorStop(1, 'rgba(255, 50, 0, 0)');
      ctx.fillStyle = emberGradient;
      ctx.beginPath();
      ctx.arc(0, 0, particle.size * 2, 0, Math.PI * 2);
      ctx.fill();
      break;
  }

  ctx.restore();
};

export default ParticleEffect;

